import { LoggerService } from "../core";
import { Service } from "../service";

export const BROWSER_DB_SVC = 'BrowserDatabaseService';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- this interface will be used for interface augmentation
export interface DbStoreType {}

interface UpgradeHandler {
    version: number;
    upgrade: (db: IDBDatabase, transaction: IDBTransaction, oldVersion: number) => void | Promise<void>;
    rollback?: (db: IDBDatabase, transaction: IDBTransaction, targetVersion: number) => void | Promise<void>;
    description?: string; // For logging/debugging
}

export interface DatabaseConfig {
    /**
     * Name of the database
     */
    name: string;
    /**
     * Version of the database
     */
    version: number;
    /**
     * Upgrades that will be applied to the database (like migrations)
     */
    upgrades: UpgradeHandler[];
    onVersionChange?: (event: IDBVersionChangeEvent) => void;
    onBlocked?: (event: Event) => void;
}

function handleRequest<T>(req: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => { resolve(req.result); };
        req.onerror = () => { reject(req.error); };
    });
}

class DatabaseTransaction {
    private _completed = false;

    constructor(private transaction: IDBTransaction) { }

    private checkActive(): void {
        if (this.transaction.error) {
            throw new Error('Transaction is no longer active');
        }
    }

    async get<K extends keyof DbStoreType, T extends DbStoreType[K]>(
        storeName: K,
        key: IDBValidKey
    ): Promise<T | undefined> {
        this.checkActive();
        const store = this.transaction.objectStore(storeName as string);
        return handleRequest<T>(store.get(key));
    }

    async put<K extends keyof DbStoreType>(
        storeName: K,
        value: DbStoreType[K],
        key?: IDBValidKey
    ): Promise<IDBValidKey> {
        this.checkActive();
        const store = this.transaction.objectStore(storeName as string);
        return handleRequest(store.put(value, key));
    }

    async delete(storeName: keyof DbStoreType, key: IDBValidKey): Promise<void> {
        const store = this.transaction.objectStore(storeName);
        return handleRequest(store.delete(key));
    }

    async getAll<StoreName extends keyof DbStoreType, T extends DbStoreType[StoreName]>(storeName: StoreName): Promise<T[]> {
        const store = this.transaction.objectStore(storeName);
        return handleRequest<T[]>(store.getAll());
    }

    async waitForCompletion(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (this._completed) { resolve(); return; }

            this.transaction.oncomplete = () => {
                this._completed = true;
                resolve();
            };
            this.transaction.onerror = () => { reject(this.transaction.error); };
            this.transaction.onabort = () => { reject(new Error('Transaction aborted')); };
        });
    }

    abort(): void {
        this.transaction.abort();
    }
}

export class IndexedDBDatabase {
    private loggerService: LoggerService = LoggerService.getInstance();
    private db: IDBDatabase | undefined;

    constructor(private config: DatabaseConfig) { }

    /**
     * Open an IndexDB database
     *
     * @returns
     */
    async open(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const request = window.indexedDB.open(this.config.name, this.config.version);

            request.onerror = () => {
                this.loggerService.error('[IndexedDBDatabase] Failed to open database:', request.error);
                reject(request.error);
            };
            request.onsuccess = () => {
                this.db = request.result;

                // Handle version change events after opening
                this.db.onversionchange = (event) => {
                    this.loggerService.warn('[IndexedDBDatabase] Database version changed by another tab');
                    // Call custom handled if provided
                    this.config.onVersionChange?.(event);
                    // Close this connection to allow the upgrade
                    this.close();
                };

                resolve();
            };
            request.onblocked = (event) => {
                this.loggerService.warn('[IndexedDBDatabase] Please close all other tabs with this site open!');
                // Call custom blocked handler if provided
                this.config.onBlocked?.(event);
            };

            // eslint-disable-next-line @typescript-eslint/no-this-alias -- in this case, there is no way to avoid aliasing
            const that = this;
            request.onupgradeneeded = async function (this, event) {
                const db = this.result;

                const transaction = this.transaction;
                const oldVersion = event.oldVersion;
                const newVersion = event.newVersion || that.config.version;

                try {
                    // Log the upgrade process
                    that.loggerService.debug(`[IndexedDBDatabase] Upgrading database from version ${oldVersion} to ${newVersion}`);

                    // Run all relevant upgrades
                    const applicableUpgrades = that.config.upgrades
                        .filter(handler => handler.version > oldVersion && handler.version <= newVersion)
                        .sort((versionA, versionB) => versionA.version - versionB.version);

                    for (const upgradeHandler of applicableUpgrades) {
                        that.loggerService.debug(`[IndexedDBDatabase] Running upgrade to version ${upgradeHandler.version}: ${upgradeHandler.description || 'No description'}`);
                        await upgradeHandler.upgrade(db, transaction, oldVersion);
                    }

                    that.loggerService.debug('[IndexedDBDatabase] Database upgrade completed successfully');
                } catch (error) {
                    that.loggerService.error('[IndexedDBDatabase] Database upgrade failed:', error);
                    if (error instanceof Error) {
                        reject(error);
                    } else {
                        reject(new Error(error));
                    }
                }
            };
        });
    }

    public isOpen(): boolean {
        try {
            return this.db !== undefined && this.db.name !== undefined;
        } catch {
            return false;
        }
    }

    createTransaction(mode: IDBTransactionMode, storeNames: (keyof DbStoreType)[]): DatabaseTransaction {
        if (!this.db) throw new Error('Database not opened');

        const transaction = this.db.transaction(storeNames, mode);
        return new DatabaseTransaction(transaction);
    }

    async runTransaction<T>(mode: IDBTransactionMode, storeNames: (keyof DbStoreType)[], operation: (tx: DatabaseTransaction) => Promise<T>): Promise<T> {
        const tx = this.createTransaction(mode, storeNames);

        try {
            return await operation(tx);
        } catch (error) {
            try {
                tx.abort();
            } catch {
                // Transaction might already be completed/aborted
            }
            throw error;
        }
    }

    close(): void {
        if (!this.db) return;

        // Remove event handlers to prevent memory leaks
        this.db.onversionchange = null;
        this.db.close();
        this.db = undefined;
    }
}

export class BrowserDatabaseService extends Service {
    public static getInstance() { return Service.create(BROWSER_DB_SVC, BrowserDatabaseService); }
    public static build() { return new BrowserDatabaseService(); }

    private instances = new Map<string, IndexedDBDatabase>();

    /**
     * Create a new {@link IndexedDBDatabase} instance
     *
     * @param config - Database config object
     * @returns database instance
     */
    createDbInstance(config: DatabaseConfig): IndexedDBDatabase {
        const key = `${config.name}_v${config.version}`;

        // If the db exists (can be opened or closed)
        let db = this.instances.get(key);

        // Otherwise, create the db instance
        db = new IndexedDBDatabase(config);
        this.instances.set(key, db);
        return db;
    }

    async deleteDatabase(name: string): Promise<void> {
        // Close any open instances first
        for (const [key, instance] of this.instances) {
            if (key.startsWith(name)) {
                instance.close();
                this.instances.delete(key);
            }
        }

        return new Promise((resolve, reject) => {
            const deleteRequest = indexedDB.deleteDatabase(name);
            deleteRequest.onsuccess = () => { resolve(); };
            deleteRequest.onerror = () => { reject(deleteRequest.error); };
        });
    }
}

/*

// Example usage with upgrades
export function createUserDatabase(): IndexedDBDatabase {
  const service = new BrowserDatabaseService();

  const config: DatabaseConfig = {
    name: 'UserAppDB',
    version: 3,
    upgrades: [
      {
        version: 1,
        upgrade: (db) => {
          // Initial setup - create users store
          const userStore = db.createObjectStore('users', { keyPath: 'id' });
          userStore.createIndex('email', 'email', { unique: true });
        }
      },
      {
        version: 2,
        upgrade: (db) => {
          // Add settings store
          db.createObjectStore('settings', { keyPath: 'key' });
        }
      },
      {
        version: 3,
        upgrade: (db, transaction) => {
          // Add new field to users - this requires data migration
          const userStore = transaction.objectStore('users');
          userStore.createIndex('createdAt', 'createdAt');

          // You could iterate through existing records and update them here
          // const request = userStore.openCursor();
          // request.onsuccess = (event) => {
          //   const cursor = (event.target as IDBRequest).result;
          //   if (cursor) {
          //     const user = cursor.value;
          //     user.createdAt = user.createdAt || new Date().toISOString();
          //     cursor.update(user);
          //     cursor.continue();
          //   }
          // };
        }
      }
    ]
  };

  return service.createDbInstance(config);
}
*/

/*

// Simple operations (existing API still works)
const user = await db.get('users', userId);
await db.put('users', updatedUser);

// Batch operations with explicit transaction
const tx = db.createTransaction('readwrite', ['users', 'settings']);
try {
    await tx.put('users', user);
    await tx.put('settings', userSettings);
    await tx.delete('cache', oldCacheKey);
    await tx.commit();
} catch (error) {
    tx.abort();
    throw error;
}

// Or using the helper method
await db.runTransaction('readwrite', ['users', 'settings'], async (tx) => {
    const user = await tx.get('users', userId);
    user.lastLogin = new Date();
    await tx.put('users', user);
    await tx.put('settings', { userId, theme: 'dark' });
    return user;
});

*/
